import React, { useState, useEffect } from 'react';
import { XCircle, Search, Eye, Calendar, User, MessageSquare } from 'lucide-react';
import { solicitudesService } from '../../../../services/api';
import OrdenTrabajoDetailModal from './OrdenTrabajoDetailModal';

const OrdenesRechazadasTab = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrden, setSelectedOrden] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    cargarRechazadas();
  }, []);

  const cargarRechazadas = async () => {
    try {
      setLoading(true);
      const res = await solicitudesService.getAll({});
      const rechazadas = (res.data || []).filter(s =>
        (s.estado === 'rechazado_cliente' || s.estado === 'rechazado_gaman') && s.vehiculo_id != null
      );
      setOrdenes(rechazadas);
    } catch (error) {
      console.error('Error cargando órdenes rechazadas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (orden) => {
    setSelectedOrden(orden);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedOrden(null);
    setShowModal(false);
  };

  const handleOrdenUpdated = () => {
    cargarRechazadas();
  };

  const ordenesFiltradas = ordenes.filter(orden =>
    orden.nombre_solicitud?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    orden.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (orden.motivo_rechazo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (orden.folio || `#${orden.id}`).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-red-50 to-rose-50 px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-red-800 flex items-center gap-2">
          <XCircle size={20} />
          Órdenes Rechazadas
        </h2>
        <p className="text-sm text-gray-600">
          Registro de órdenes de trabajo que han sido rechazadas con su respectivo motivo
        </p>
      </div>

      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por orden, folio o motivo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>
          <span className="text-sm text-gray-500 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
            Total: {ordenes.length} rechazada(s)
          </span>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : ordenesFiltradas.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <XCircle size={48} className="mx-auto mb-3 text-gray-300" />
            <p className="font-medium text-gray-600">No hay órdenes rechazadas</p>
            <p className="text-sm mt-1">
              Las órdenes que sean rechazadas desde el detalle de orden aparecerán aquí
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {ordenesFiltradas.map(orden => (
              <div
                key={orden.id}
                className="p-4 border border-red-200 rounded-lg hover:border-red-400 hover:bg-red-50/50 transition-all cursor-pointer shadow-sm"
                onClick={() => handleOpenModal(orden)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="font-semibold text-gray-800">{orden.nombre_solicitud}</h3>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold text-white bg-red-500">
                        Rechazado
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-600 mb-2">
                      <div className="flex items-center gap-1.5">
                        <Calendar size={13} className="text-gray-400" />
                        <span>{orden.fecha_creacion ? new Date(orden.fecha_creacion).toLocaleDateString() : 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <User size={13} className="text-gray-400" />
                        <span>Folio: {orden.folio || `#${orden.id}`}</span>
                      </div>
                    </div>

                    {orden.motivo_rechazo && (
                      <div className="flex items-start gap-2 mt-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                        <MessageSquare size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-red-700 line-clamp-3">{orden.motivo_rechazo}</p>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-2 line-clamp-1">
                      {orden.descripcion?.split('\n')[0] || ''}
                    </p>
                  </div>
                  <Eye size={18} className="text-gray-400 flex-shrink-0 ml-3" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && selectedOrden && (
        <OrdenTrabajoDetailModal
          orden={selectedOrden}
          onClose={handleCloseModal}
          onUpdated={handleOrdenUpdated}
        />
      )}
    </div>
  );
};

export default OrdenesRechazadasTab;
